import { useEffect, useRef, useState } from 'react';
import type { ReactNode, PointerEvent as ReactPointerEvent } from 'react';
import { useUiStore } from '../../store/ui';
import { dbg } from '../../lib/debug';
import { ChevronRight } from './icons';

type Props = {
  open: boolean;
  onClose: () => void;
  title?: string;
  /** When set, a back arrow is shown next to the title (step ← in a flow). */
  onBack?: () => void;
  /** When false, swipe / backdrop do not dismiss (e.g. while a request is in flight). */
  dismissible?: boolean;
  children: ReactNode;
};

const DURATION = 500; // keep in sync with the duration-500 transition
const CLOSE_THRESHOLD = 90; // px dragged down before the sheet dismisses
const ENGAGE = 6; // px before a gesture is decided as drag vs scroll

function isInteractiveTarget(t: EventTarget | null): boolean {
  const el = t as HTMLElement | null;
  return !!el?.closest?.('input, textarea, select, button, a, [role="button"]');
}

/** Slide-up bottom sheet with a dimmed backdrop and swipe-to-close. The whole
 *  sheet is draggable while its content is scrolled to the top; once scrolled,
 *  the gesture scrolls the content instead (iOS / Telegram behaviour). */
export function BottomSheet({ open, onClose, title, onBack, dismissible = true, children }: Props) {
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const dismissibleRef = useRef(dismissible);
  dismissibleRef.current = dismissible;

  // Hide the bottom nav while a sheet is open.
  useEffect(() => {
    if (!open) return;
    openSheet();
    return () => closeSheet();
  }, [open, openSheet, closeSheet]);

  // TEMPORARY diagnostics breadcrumb only — no layout side-effects.
  useEffect(() => {
    dbg(`sheet open=${open}${title ? ` "${title}"` : ''}`);
  }, [open, title]);

  // Defer the visible (open) state by one frame so a sheet that mounts already
  // open still animates from its closed position instead of appearing instantly.
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => setShow(true));
      return () => cancelAnimationFrame(id);
    }
    setShow(false);
  }, [open]);

  // Animate out, then notify the parent (which may unmount us).
  const closingRef = useRef(false);
  const close = (reason = 'unknown') => {
    if (closingRef.current) return;
    if (!dismissibleRef.current) {
      setDragY(0);
      dragYRef.current = 0;
      return;
    }
    dbg(`sheet close() reason=${reason}`);
    closingRef.current = true;
    // Dismiss the soft keyboard with the sheet — a still-focused input would keep
    // it (and Telegram's shrunken webview) up while the sheet animates away.
    const ae = document.activeElement;
    if (ae instanceof HTMLElement && sheetRef.current?.contains(ae)) ae.blur();
    setDragging(false);
    setDragY(0);
    setShow(false);
    window.setTimeout(() => {
      closingRef.current = false;
      onClose();
    }, DURATION);
  };

  // Swipe-down to dismiss — handled on the sheet itself.
  const sheetRef = useRef<HTMLDivElement>(null);
  const [dragY, setDragY] = useState(0);
  const [dragging, setDragging] = useState(false);
  const draggingRef = useRef(false);
  const decidedRef = useRef(false); // gesture resolved as a scroll → ignore for drag
  const fromHandleRef = useRef(false); // gesture started on the grab handle → always closes
  const dragYRef = useRef(0);
  const startX = useRef(0);
  const startY = useRef(0);
  const downRef = useRef(false); // pointer is actually pressed (mouse fires move on hover too)

  // Once we've committed to dragging the sheet down, stop the WebView from also
  // scrolling natively — otherwise it fires pointercancel and kills the drag
  // (notably when the gesture starts over a horizontal card carousel). React's
  // onTouchMove is passive, so we attach a non-passive listener by hand.
  useEffect(() => {
    const el = sheetRef.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => {
      if (draggingRef.current) e.preventDefault();
    };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, [open]);

  // Diagnostics only. Do NOT call scrollIntoView here — that was added in
  // 9b76a69 (2026-07-11) and fights Telegram iOS keyboard handling inside a
  // position:fixed sheet (device log: focus → WebView BOOT/reload).
  useEffect(() => {
    const el = sheetRef.current;
    if (!el || !open) return;
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target || !/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;
      dbg(`focusin <${target.tagName.toLowerCase()}>`);
    };
    el.addEventListener('focusin', onFocusIn);
    return () => el.removeEventListener('focusin', onFocusIn);
  }, [open]);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return; // primary button only
    // Never start a sheet-drag from a form control — on iOS the keyboard open
    // shifts clientY and the body drag handler would steal the gesture.
    if (!fromHandleRef.current && isInteractiveTarget(e.target)) {
      downRef.current = false;
      decidedRef.current = true;
      return;
    }
    downRef.current = true;
    startX.current = e.clientX;
    startY.current = e.clientY;
    draggingRef.current = false;
    decidedRef.current = false;
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!downRef.current || closingRef.current) return; // ignore hover moves
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;
    if (!draggingRef.current) {
      if (decidedRef.current) return;
      // Content not at the top → let it scroll, don't drag the sheet. Unless the
      // gesture began on the grab handle, which always drags the sheet to close
      // (so a sheet with scrollable content can still be dismissed).
      if (!fromHandleRef.current && (sheetRef.current?.scrollTop ?? 0) > 0) {
        decidedRef.current = true;
        return;
      }
      if (Math.abs(dx) < ENGAGE && Math.abs(dy) < ENGAGE) return;
      // A sideways swipe (card carousel) must not capture the pointer. Capture
      // plus the non-passive touchmove preventDefault cancels that scroll.
      if (!fromHandleRef.current && Math.abs(dx) > Math.abs(dy)) {
        decidedRef.current = true;
        return;
      }
      if (dy > ENGAGE) {
        draggingRef.current = true;
        setDragging(true);
        try {
          e.currentTarget.setPointerCapture(e.pointerId);
        } catch {
          /* ignore */
        }
      } else if (dy < -ENGAGE) {
        decidedRef.current = true;
        return;
      } else {
        return;
      }
    }
    dragYRef.current = dy > 0 ? dy : 0;
    setDragY(dragYRef.current);
  };
  const endDrag = () => {
    const fromHandle = fromHandleRef.current;
    downRef.current = false;
    decidedRef.current = false;
    fromHandleRef.current = false;
    if (!draggingRef.current) return;
    draggingRef.current = false;
    setDragging(false);
    if (dragYRef.current > CLOSE_THRESHOLD) close(fromHandle ? 'handle-drag' : 'body-drag');
    else setDragY(0);
    dragYRef.current = 0;
  };

  return (
    // overflow-hidden: a closed sheet is "hidden" by translate-y-full, i.e. it
    // hangs below this container — clip it so no keyboard/viewport resize can
    // ever reveal it (on iOS the shrunken viewport exposed the hidden Withdraw
    // sheet above the keyboard).
    <div className="fixed inset-0 z-40 overflow-hidden" style={{ pointerEvents: open ? 'auto' : 'none' }}>
      {/* Dim overlay. Standard sheet UX: while the keyboard is up, the first
          outside tap only dismisses the keyboard; the next one closes the sheet. */}
      <div
        onClick={() => {
          if (!dismissibleRef.current) return;
          const ae = document.activeElement;
          if (
            ae instanceof HTMLElement &&
            sheetRef.current?.contains(ae) &&
            /^(INPUT|TEXTAREA)$/.test(ae.tagName)
          ) {
            ae.blur();
            return;
          }
          close('backdrop');
        }}
        className={`absolute inset-0 bg-black/30 transition-opacity duration-500 ${
          show ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* Sheet — plain `bottom-0`, NO JavaScript keyboard handling. On iOS
          Telegram natively resizes the webview to end at the keyboard top when an
          input focuses, so a bottom-anchored sheet lands above the keyboard by
          itself. Every scripted alternative was tried and reverted: visualViewport
          lifts race the native resize (double-lift, gaps, blinking),
          scrollIntoView / rect patches crashed the WebView, and the
          `interactive-widget=resizes-content` meta stranded fixed elements behind
          the keyboard. max-h is a PERCENTAGE of the fixed inset-0 container so it
          tracks the webview frame exactly as Telegram resizes it (vh/dvh lag or
          re-layout mid-animation). */}
      <div
        ref={sheetRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        className={`absolute inset-x-0 bottom-0 max-h-[92%] touch-[pan-x_pan-y] overflow-y-auto overscroll-contain rounded-t-[28px] bg-white px-4 pb-[calc(var(--tg-safe-bottom)_+_2rem)] shadow-[0px_-8px_40px_rgba(0,0,0,0.12)] transition-transform duration-500 ease-out ${
          // translate-y-full parks the sheet just under the viewport, but the
          // upward shadow still paints ~40px into the page (the bottom band on
          // cards / staking). The extra 64px puts that shadow past the clip.
          show ? 'translate-y-0' : 'translate-y-[calc(100%+64px)]'
        }`}
        style={dragging ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
      >
        {/* Grab handle + optional back / title — dragging here always closes,
            even when the content below is scrolled. */}
        <div
          onPointerDown={() => {
            fromHandleRef.current = true;
          }}
          className="sticky top-0 z-10 -mx-4 cursor-grab rounded-t-[28px] bg-white px-4 pb-2 pt-3 active:cursor-grabbing"
        >
          <div className="mx-auto h-1 w-10 rounded-full bg-black/15" />
          {(title || onBack) && (
            <div className="relative mt-4 flex h-8 items-center justify-center">
              {onBack && (
                <button
                  type="button"
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={onBack}
                  className="absolute left-0 flex h-8 w-8 items-center justify-center rounded-full bg-black/[0.05] text-ink active:bg-black/[0.1]"
                  aria-label="Back"
                >
                  <ChevronRight size={18} className="rotate-180" />
                </button>
              )}
              {title && <h3 className="text-[16px] font-medium text-ink">{title}</h3>}
            </div>
          )}
        </div>
        {children}
      </div>
    </div>
  );
}
